// @flow
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowDown2 from "../../../../assets/images/icons/arrowDown2";
import Edit from "../../../../assets/images/icons/loan/edit";
import Confirmation from "../../../../components/confirmation/confirmation";
import TruncateDescription from "../../../../components/truncateDescription";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../../helpers/errorHandling";
import { useMuiGridServerSide } from "../../../../hooks/useMuiGridServerSide";
import { DataGridPremium, } from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import useUserAction from "../../../../hooks/user/useUserAction";
import { ROUTE_LOAN_APPLICATION } from "../../../loanApplication/routes/routes";
import { CUSTOMERS } from "../../locales/namespaces";
import {
	Typography,
	Box,
	Button,
	Divider,
	useMediaQuery,
	Stack,
	Autocomplete,
	Checkbox,
	FormControl,
	TextField,
	FormLabel, Select, MenuItem
} from "@mui/material";
import { Link } from 'react-router-dom';
import { getRouteUrl } from "../../../../helpers/getRouteUrl";
import {
	ROUTE_PAGE_CUSTOMERS_DETAILS,
} from "../../routes/routes";
import DataGridWrapper from "../../../../components/dataGridWrapper/dataGridWrapper";
import { withPermissionHOC } from "../../../permission/components/permissionHOC/permissionHOC";
import Breadcrumb from "../../../../components/breadcrumb/breadcrumb";
import { ROUTE_PAGE_HOME } from "../../../../routers/routes";
import { API, CustomerApplicationsLanguages, CustomerApplicationsStatus } from "../../../../helpers/constants";
import moment from "moment";
import { muiGridLocales } from "../../../../helpers/muiGridLocales";
import MainCard from "../../../../theme/mantis/components/mainCard";
import { CustomPagination } from '../../../../components/dataGridCustom/customPagination'
import { AuthenticatedLink } from "../../../../components/authenticatedLink/authenticatedLink";
import { textTranslate } from "../../../../helpers/helpers";
import useUserData from "../../../../hooks/user/useUserData";
import DownloadIcon from "../../../../assets/images/icons/buttonsIcons/downloadIcon";
import { UnsortedIcon } from "../../../../assets/images/icons/sortIcon";
import HomeBreadcrumb from "../../../../assets/images/icons/homeBreadcrumb";
import { Capacitor } from "@capacitor/core";

export const initialFilters = {
	status: []
}

const Applications = () => {
		const { t } = useTranslation(CUSTOMERS)
		const { language, isLoggedIn, token } = useUserData()
		const { userLogout } = useUserAction()
		const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
		const [filters, setFilters] = useState(initialFilters)
		const [confirmation, setConfirmation] = useState({
			open: false,
			status: null,
			lang: null,
			prevStatus: null,
			id: null
		})
		const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false)
		useEffect(() => {
			document.title = t('IMS Financial Consulting | Loan Applications')
		}, [t])
		
		const {
			isLoading,
			data,
			total,
			page,
			pageSize,
			onPaginationModelChange,
			onSortModelChange,
			onFilterModelChange,
			dataManager
		} = useMuiGridServerSide({
			url: 'customer/application/data',
			initialPageSize: 50,
			initialSortModel: [{ name: 'LastModifiedAt', direction: 'descending' }],
			otherDataParams: {
				...(filters.status && filters.status.length > 0 && { status: filters.status.map(i => (i.id))?.join(',') }),
			}
		})
		
		const updateApplicationStatusOrLang = useCallback((newValue) => {
			setLoadingStatusUpdate(true)
			axios({
				method: 'post',
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
				url: `${API}/customer/application/status`,
				data: {
					id: newValue.id,
					Status: newValue.status,
					Lang: newValue.lang
				}
			}).then((response) => {
				setLoadingStatusUpdate(false)
				const onSuccess = () => (
					setConfirmation({
						open: false,
						status: null,
						lang: null,
						prevStatus: null,
						id: null
					})
				)
				dataManager({
					status: filters.status && filters.status.length > 0 ? filters.status.map(i => i.id).join(',') : null
				}, onSuccess)
			}).catch(error => {
				setLoadingStatusUpdate(false)
				enqueueSnackbar(handleAxiosErrors(error, newValue.status ? t('Failed to update application status.') : t('Failed to update application lang.'), {
						variant: 'error'
					}
				))
				dataManager({
					status: filters.status && filters.status.length > 0 ? filters.status.map(i => i.id).join(',') : null
				})
				logoutUnauthorizedUser(error, isLoggedIn, userLogout)
			})
		}, [dataManager, filters.status, isLoggedIn, t, token, userLogout])
		
		const onCloseConfirmation = useCallback(() => {
			setConfirmation({
				open: false,
				status: null,
				lang: null,
				prevStatus: null,
				id: null
			})
			dataManager({
				status: filters.status && filters.status.length > 0 ? filters.status.map(i => i.id).join(',') : null
			})
		}, [dataManager, filters.status])
		
		const columns = useMemo(() => [
			{
				field: 'Export',
				type: 'actions',
				headerName: t('Export'),
				width: 100,
				renderCell: (params) => {
					if (params.rowNode.type === 'group') {
						return params.value;
					}
					return (
						<>
							<IconButton
								component={Link}
								variant={'info'}
								to={getRouteUrl(ROUTE_LOAN_APPLICATION, {
									id: params.row.id
								})}
								disabled={params.row.Status !== '1'  && params.row.Status !== '2'}
								sx={{
									opacity:  (params.row.Status === '1' || params.row.Status === '2') ? 1 : 0.5,
								}}
							>
								<Edit color={'#0000008a'}/>
							</IconButton>
							<AuthenticatedLink
								url={`${API}/customer/application/export/${params.row.id}`}
								// filename={`application-${params.row.CustName}.csv`}
							>
								<IconButton
									size={"small"}
									variant={"outlined"}
									color={'info'}
									target={!Capacitor.isNativePlatform() && "_blank"}
								>
									<DownloadIcon/>
								</IconButton>
							</AuthenticatedLink>
						</>
					
					)
				},
			},
			{
				field: 'CustName',
				headerName: t('Full name'),
				minWidth: 250,
				flex: 1,
				renderCell: (params) => {
					if (params.rowNode.type === 'group') {
						return params.value;
					}
					return (
						<Button
							sx={{
								color: '#485776',
								fontSize: '14px',
								fontStyle: 'normal',
								fontWeight: 700,
								lineHeight: 'normal',
								'&:hover': {
									backgroundColor: 'rgba(244, 245, 250, 0.38)',
								},
							}}
							component={Link}
							variant={'info'}
							to={getRouteUrl(ROUTE_PAGE_CUSTOMERS_DETAILS, {
								id: params.row.CustId ? params.row.CustId : params.row.id
							})}
						>
							{params.value}
						</Button>
					)
				},
			},
			{
				field: 'Bank',
				headerName: t('Bank'),
				minWidth: 150,
				flex: 1
			},
			{
				field: 'Status',
				headerName: t('Status'),
				minWidth: 200,
				filterable: false,
				flex: 1,
				editable: true,
				type: 'singleSelect',
				valueOptions: CustomerApplicationsStatus.map(status => ({
					value: status.id,
					label: textTranslate(language)({ en: status.Description.en, el: status.Description.el }),
				})),
				renderEditCell: (params) => {
					return (
						<Select
							fullWidth
							variant={'standard'}
							sx={{ marginTop: '10px' }}
							native={Capacitor.getPlatform() === 'android'}
							size={"small"}
							name={"status-selection"}
							value={params.value}
							onChange={(event) => {
								setConfirmation({
									open: true,
									status: event.target.value,
									lang: null,
									prevStatus: params.value,
									id: params.row.id
								})
							}}
							IconComponent={(props) => (
								<ArrowDown2
									{...props}
									style={{ width: '14px', height: '10px', marginRight: '6px' }}
									className="custom-arrow"
								/>
							)}
						>
							{CustomerApplicationsStatus.map(status => (
								<MenuItem
									key={`${status.id}`}
									value={status.id}
								>
									{textTranslate(language)({
										en: status.Description.en,
										el: status.Description.el
									})}
								</MenuItem>
							))}
						</Select>
					)
				}
			},
			{
				field: 'Lang',
				headerName: t('Language'),
				minWidth: 150,
				flex: 1,
				editable: true,
				type: 'singleSelect',
				valueOptions: CustomerApplicationsLanguages.map(lang => ({
					value: lang.id,
					label: textTranslate(language)({ en: lang.Description.en, el: lang.Description.el }),
				})),
				renderEditCell: (params) => {
					return (
						<Select
							fullWidth
							variant={'standard'}
							sx={{ marginTop: '10px' }}
							native={Capacitor.getPlatform() === 'android'}
							size={"small"}
							name={"status-selection"}
							value={params.value}
							onChange={(event) => {
								setConfirmation({
									open: true,
									status: null,
									lang: event.target.value,
									prevStatus: params.value,
									id: params.row.id
								})
							}}
							IconComponent={(props) => (
								<ArrowDown2
									{...props}
									style={{ width: '14px', height: '10px', marginRight: '6px' }}
									className="custom-arrow"
								/>
							)}
						>
							{CustomerApplicationsLanguages.map(lang => (
								<MenuItem
									key={`${lang.id}`}
									value={lang.id}
								>
									{textTranslate(language)({
										en: lang.Description.en,
										el: lang.Description.el
									})}
								</MenuItem>
							))}
						</Select>
					)
				}
			},
			{
				field: 'email',
				headerName: t('Email'),
				minWidth: 200,
				flex: 1,
			},
			{
				field: 'MobilePhone',
				headerName: t('Mobile Number'),
				minWidth: 120,
				flex: 1
			},
			{
				field: 'LastModifiedAt',
				headerName: t('Last Modified Date'),
				minWidth: 200,
				flex: 1,
				
				type: 'date',
				valueFormatter: (params) => {
					return (params.value ? moment(params.value).format('DD-MM-YYYY HH:mm') : null)
				}
			},
			{
				field: 'Message',
				headerName: t('Error Message'),
				minWidth: 300,
				flex: 1,
				renderCell: (params) => {
					return <TruncateDescription value={params.value}/>
				},
			},
		], [language, t])
		
		return (
			<Box className={'customer-applications'}>
				{!matchDownSM &&
					<Breadcrumb>
						<Breadcrumb.Link iconComponent={HomeBreadcrumb} to={getRouteUrl(ROUTE_PAGE_HOME)}/>
						<Breadcrumb.Item>
							{t('Loan Applications')}
						</Breadcrumb.Item>
					</Breadcrumb>
				}
				<MainCard
					sx={{ mt: 2 }}
					title={
						<Typography variant="h4">
							{t('Loan Applications')}
						</Typography>
					}
				>
					<Stack spacing={1}>
						<FormControl
							fullWidth
							variant="outlined"
						>
							<FormLabel htmlFor="status">{t('Status')}</FormLabel>
							<Autocomplete
								multiple
								popupIcon={<ArrowDown2/>}
								options={CustomerApplicationsStatus}
								disableCloseOnSelect
								isOptionEqualToValue={(opt, val) => {
									return opt.id === val.id || !val
								}}
								value={filters.status}
								onChange={(event, newValue, reason) => {
									setFilters(prev => ({ ...prev, status: newValue }))
									dataManager({
										status: newValue && newValue.length > 0 ? newValue.map(i => i.id).join(',') : null
									})
								}}
								getOptionLabel={option => textTranslate(language)({
									en: option.Description.en,
									el: option.Description.el
								})}
								renderOption={(props, option, { selected }) => {
									const { key, ...other } = props
									return (
										(
											<li {...other} key={option.id}>
												<Checkbox
													style={{ marginRight: 8 }}
													checked={selected}
												/>
												{textTranslate(language)({
													en: option.Description.en,
													el: option.Description.el
												})}
											</li>
										)
									)
								}}
								getOptionKey={(option) => (option?.id)}
								renderInput={(params) => {
									return (
										<TextField
											placeholder={t("Choose status")}
											size={"small"}
											{...params}
											inputProps={{
												...params.inputProps,
												autoComplete: 'off',
											}}
										/>
									)
								}}
								size={'small'}
							/>
						</FormControl>
					</Stack>
					<Divider sx={{ mb: 2 }}/>
					<DataGridWrapper>
						<DataGridPremium
							getRowClassName={(params) => {
								return (
									params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
								)
							}}
							sx={{
								"& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
									visibility: "visible",
									width: "auto"
								},
								"& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon": {
									opacity: 0.5
								},
								".MuiDataGrid-aggregationColumnHeaderLabel": {
									display: "none"
								},
								maxHeight: 650,
							}}
							columns={columns}
							density={'compact'}
							rows={data}
							pagination
							paginationModel={{ page, pageSize }}
							paginationMode='server'
							onPaginationModelChange={onPaginationModelChange}
							rowCount={Number(total)}
							rowsPerPageOptions={[5, 10, 15, 20, 50, 100]}
							sortingMode="server"
							onSortModelChange={(sortModel) => onSortModelChange(sortModel)}
							filterMode='server'
							onFilterModelChange={(filterModel, details) => onFilterModelChange(filterModel, details)}
							loading={isLoading}
							localeText={muiGridLocales(t)}
							slots={{
								pagination: CustomPagination,
								columnUnsortedIcon: UnsortedIcon
							}}
							disableAggregation={true}
							disableRowGrouping={true}
						/>
					</DataGridWrapper>
				</MainCard>
				<Confirmation
					open={confirmation.open}
					handleDialog={onCloseConfirmation}
					onNo={onCloseConfirmation}
					onYes={() => {
						updateApplicationStatusOrLang(confirmation.status ? {
								id: confirmation.id,
								status: confirmation.status,
							} :
							confirmation.lang ? { id: confirmation.id, lang: confirmation.lang } : {})
					}}
					loading={loadingStatusUpdate}
					title={confirmation.status ? t('Please confirm application status change!') :
						confirmation.lang ? t('Please confirm application lang change!') : ''}
				/>
			</Box>
		);
	}
;

export default withPermissionHOC(Applications, ['access_customer_applications'])